import type { Messages } from './en';

/** 第一階段內容：爸爸的聊天、英文練習、電視新聞、社群媒體安全（繁體中文・台灣）。 */
export const zhTWExtra: Pick<Messages, 'dadChat' | 'english2' | 'news' | 'social'> = {
  dadChat: {
    name: '爸爸 👔',
    preview: '爸爸傳送了一張圖片',
    /** 爸爸每天傳的長輩圖上的大字（每天一張，輪流）。 */
    cards: [
      { big: '早安', sub: '微笑面對每一天，世界也會對你微笑' },
      { big: '平安喜樂', sub: '祝您身體健康、好運連連' },
      { big: '週五快樂', sub: '認真工作，好好休息' },
      { big: '週末愉快', sub: '家人是最珍貴的寶藏' },
      { big: '晚安', sub: '好好休息，一夜好夢' },
      { big: '身體健康', sub: '多喝水，多走路' },
      { big: '福氣滿滿', sub: '祝您萬事如意、事事順心' },
      { big: '早安', sub: '新的一天，新的希望' },
    ],
    cardCaption: ['🌸 送給我親愛的家人！', '☀️ 祝大家有美好的一天！', '🙏 祝福您！', '💪 加油加油！'],
    choices: {
      thanks: '謝謝爸爸！❤️',
      cardBack: '回傳一張長輩圖 🌻',
      askDay: '爸，今天上班還好嗎？',
      photo: '傳一張今天的照片給爸爸 📸',
    },
    replies: {
      thanks: ['不客氣！爸爸愛你！', '嘿嘿，你喜歡爸爸就開心！', '記得要吃青菜喔！🥦'],
      cardBack: ['哇！好漂亮！你去哪裡找的？😄', '爸爸存起來了！現在要轉傳給爺爺。', '一天收到兩張圖！好幸運！'],
      askDay: ['很忙，但看到你的訊息就好多了！', '開了好多會……一直在想晚上跟你們吃飯。', '老闆今天說我做得很好！💪'],
      photo: ['拍得真好！你是真正的攝影師！', '看起來今天很好玩。出門要注意安全喔！', '爸爸把它設成手機桌布了！'],
    },
    /** 長輩常轉傳的謠言——玩家要學會溫柔地幫忙更正。 */
    rumors: [
      {
        text: '⚠️ 緊急！！請轉傳給 10 個人，不然你的手機明天會被鎖住！！！',
        correct: '爸，這是連鎖訊息啦。手機不會因為這樣被鎖住 😊',
        share: '好，我也來轉傳！',
        dadOk: '喔！真的嗎？謝謝你告訴我，那我就不轉了。',
        dadBad: '太好了，我已經傳給 30 個人了！😅',
        lesson: '用「不轉就會倒楣」來威脅的連鎖訊息一定是假的，不要轉傳。',
      },
      {
        text: '醫生說：每小時喝一杯熱檸檬水，可以治好所有病毒！快分享給大家！',
        correct: '爸，我們先查查可靠的衛教網站。一種飲料不可能治好所有病毒。',
        share: '哇，我要告訴朋友！',
        dadOk: '你說得對，我沒有查消息是哪裡來的。真聰明！',
        dadBad: '對！我要幫整個辦公室泡檸檬水！',
        lesson: '健康「神奇療法」傳得最快。要查官方資料，也要問醫生。',
      },
      {
        text: '免費！點這個連結就能領政府發的 5000 元。只要輸入你的金融卡資料！',
        correct: '爸，不要點！政府絕對不會用訊息跟你要卡片資料。',
        share: '免費的錢？點下去！',
        dadOk: '還好你有告訴我，我現在馬上刪掉。',
        dadBad: '嗯，網頁叫我輸入密碼……好奇怪喔。',
        lesson: '真正的機關絕不會用訊息要你的卡號或密碼，這是釣魚詐騙。',
      },
    ],
    rumorPrompt: '爸爸轉傳了一則訊息：',
  },

  english2: {
    title: '📚 英文角落',
    tabs: { learn: '學習', review: '複習', spell: '拼字', talk: '對話', read: '閱讀' },
    learnHint: '點一下單字可以聽發音。練習過的單字會加入複習卡組。',
    reviewHint: '先想想英文單字是什麼，再翻開答案。',
    reviewNone: '現在沒有要複習的單字——太棒了！先去學新單字吧。',
    reviewDue: (n: number) => `有 ${n} 個單字要複習`,
    show: '顯示答案',
    knew: '✅ 我會了',
    again: '🔁 再一次',
    choose: '哪個單字是這個意思？',
    spellHint: '先聽發音，再依序點字母。',
    spellOk: '拼得完全正確！',
    spellBad: '差一點——再試一次！',
    undo: '⌫',
    hear: '🔊 聽發音',
    next: '下一個 ➜',
    talkHint: '選出最適合的英文回答。每一句都可以聽喔！',
    talkPick: '選擇一個情境：',
    talkGood: '回答得很好！',
    talkBad: '嗯，這句不太適合，換一句試試。',
    talkDone: (title: string) => `對話完成：${title}`,
    hereFirst: '📍 就在這裡',
    mastered: (n: number, total: number) => `已精通 ${n}/${total}`,
    categories: {
      school: '學校', library: '圖書館', food: '食物與咖啡館', shopping: '購物', sports: '運動',
      health: '健康', home: '家', feelings: '心情', safety: '網路安全', nature: '大自然',
    },
    /** 每個英文角色扮演情境的簡短目標（對話本身是英文）。 */
    scenarios: {
      cafe: '在咖啡館點飲料',
      library: '借一本書',
      store: '在商店買點心',
      nurse: '告訴護理師你哪裡不舒服',
      directions: '問路',
      newFriend: '向新朋友自我介紹',
      restaurant: '在餐廳點晚餐',
      sports: '參加球隊練習',
      gift: '挑選生日禮物',
      phone: '有禮貌地接電話',
    },
    meanings: {
      classroom: '教室', homework: '作業', teacher: '老師', pencil: '鉛筆', eraser: '橡皮擦', schedule: '課表',
      borrow: '借（入）', return: '歸還', quiet: '安靜的', novel: '小說', dictionary: '字典', shelf: '書架',
      menu: '菜單', order: '點餐', delicious: '好吃的', thirsty: '口渴的', bill: '帳單', breakfast: '早餐',
      price: '價格', cheap: '便宜的', expensive: '昂貴的', receipt: '收據；發票', discount: '折扣', cashier: '收銀員',
      team: '隊伍', practice: '練習', score: '分數；得分', swim: '游泳', racket: '球拍', referee: '裁判',
      fever: '發燒', headache: '頭痛', medicine: '藥', rest: '休息', nurse: '護理師', bandage: '繃帶',
      kitchen: '廚房', laundry: '要洗的衣服', chores: '家事', neighbor: '鄰居', family: '家人', dinner: '晚餐',
      nervous: '緊張的', proud: '驕傲的', grateful: '感激的', lonely: '寂寞的', excited: '興奮的', calm: '冷靜的',
      password: '密碼', privacy: '隱私', stranger: '陌生人', report: '檢舉；回報', block: '封鎖', scam: '詐騙',
      blossom: '（樹上的）花', pond: '池塘', breeze: '微風', butterfly: '蝴蝶', sunset: '夕陽', puddle: '水坑',
    },
  },

  news: {
    title: '📺 小鎮新聞 24',
    live: '直播',
    anchor: '主播',
    quiz: '真新聞還是假新聞？',
    real: '✅ 真的',
    fake: '❌ 假的',
    right: '答對了！',
    wrong: '不太對喔！',
    nextStory: '下一則 ▶',
    done: '今天的新聞就到這裡，謝謝收看！',
    ticker: '天氣：晴時多雲，微風徐徐 · 提醒您：分享前先查證消息來源 ·',
    stories: [
      { head: '小鎮公園櫻花提早盛開', body: '溫暖的天氣讓櫻花提早一週綻放，許多家庭在粉紅色的櫻花樹下野餐。', real: true, why: '這則新聞來自地方新聞團隊，有照片也有日期。是正常、可以查證的新聞。' },
      { head: '震驚！吃巧克力一週長高 10 公分！', body: '網路上一支影片聲稱，「祕密巧克力飲食法」能讓孩子一夜長高。', real: false, why: '誇張的保證、沒有具名的醫生，還有「祕密」妙招——都是典型的假新聞特徵。' },
      { head: '學校圖書館新增 500 本新書', body: '多虧了義賣活動，國中圖書館添購了新的漫畫、小說和科普書。', real: true, why: '具體又平常的事實，學校可以證實。' },
      { head: '明星免費送手機——只要分享這篇貼文！', body: '一篇貼文說，有位知名歌手會送手機給每個分享並留言的人。', real: false, why: '要你分享才能抽獎的贈品多半是騙互動的詐騙。真正的品牌會在官方頁面公告。' },
      { head: '8 號公車下個月調整時刻表', body: '市政府宣布，上下學時段公車將每 10 分鐘一班。', real: true, why: '由市政府交通單位公布——你可以上他們的網站查證。' },
      { head: '科學家說今晚月亮會變成綠色', body: '一則瘋傳的訊息說，千年一見的綠色月亮今晚將首次出現。', real: false, why: '沒有提到任何科學家或天文台的名字，還利用大家的興奮心情。去天文網站查查看吧。' },
      { head: '警方提醒：小心假冒的包裹簡訊', body: '「您的包裹待領取，請在此付款」這類簡訊是新的詐騙手法，千萬別點連結。', real: true, why: '這是官方的警示，也是很好的建議：絕對不要透過簡訊裡的連結付款。' },
      { head: '匿名專家：喝可樂能治感冒', body: '一則連鎖訊息說，可樂治感冒比吃藥還有效。', real: false, why: '「匿名專家」就是警訊。醫療建議應該來自真正的醫生。' },
      { head: '小鎮公園池塘新種睡蓮、放養魚兒', body: '志工們種下睡蓮並放流本地魚種，讓池塘保持健康。', real: true, why: '這是地方活動，有具名的志工和照片。' },
    ],
  },

  social: {
    snapshot: '📸 拍照',
    upload: '🖼️ 上傳照片',
    captionTitle: '加上說明文字',
    captions: ['天氣好好！☀️', '看看我的點心 🍙', '和好朋友們 💛', '我的寵物超可愛 🐰', '讀書時間 📚'],
    posted: '發布成功！朋友們會看到喔。',
    likes: (n: number) => `❤️ ${n}`,
    comments: '留言',
    writeComment: '留言',
    you: '你',
    friendComments: {
      0: ['好漂亮！😍', '這是哪裡？', '天氣超讚的！'],
      1: ['好好吃的樣子！留一個給我！', '害我肚子餓了 😂', '看起來超美味！'],
      2: ['好想你們！', '好朋友！💛', '下次也約我啦！'],
      3: ['好～可～愛！！🥰', '牠叫什麼名字？', '我也想養寵物！'],
      4: ['你好認真！💪', '要不要一起讀書？', '考試加油！'],
    } as Record<number, string[]>,
    replyOptions: ['謝謝！😊', '哈哈對啊！', '下次一起去吧！'],
    /** 朋友依照你留言的內容回覆。 */
    replies: {
      question: ['好問題！我覺得是耶 🤔', '嗯，我問一下我媽再跟你說！', '對啊！下次要不要一起來？'],
      thanks: ['不客氣！🥰', '隨時都可以，朋友！💛', '小事一樁！'],
      laugh: ['哈哈哈我也是 😂', '不要啦，我在上課一直笑！🤣', '笑死！'],
      love: ['哇～我也愛你！💖', '你最棒了！🥰', '永遠的好朋友！'],
      food: ['害我好餓 🤤', '留一口給我！', '看起來好好吃！'],
      plan: ['好啊！這個週末就來！', '算我一份！🙌', '我先問一下爸媽！'],
      sad: ['怎麼了，你還好嗎？🫂', '晚點要聊聊嗎？我在喔。', '給你一個大大的擁抱！'],
      generic: ['讚！😊', '完全同意！', '哈哈，你好好笑！', '好酷！✨'],
    },
    friendNames: ['小雨', 'Emma', '小傑', '阿明'],
    lessonTitle: '🛡️ 我們學到了什麼？',
    cases: {
      fakeFriend: {
        author: '「Mia，13 歲 ✨」',
        tag: '⚠️ 交友邀請',
        content: '嗨！！你看起來好酷喔！我是新來的。我們可以私訊聊天嗎？你讀哪間學校？🥺',
        safe: '不要說——先問爸媽',
        risky: '告訴她我的學校和班級',
        safeMsg: '做得好。你無法知道帳號背後到底是誰。絕對不要透露你的學校、住址或作息。',
        riskyMsg: '小心！帳號可能是假的——有些大人會假裝成小孩。個人資料要保密。',
        lesson: '剛認識的網友一下子問很多私人問題，就是警訊。去問你信任的大人。',
      },
      romance: {
        author: '「Leo 💕」',
        tag: '⚠️ 私訊',
        content: '我覺得我真的很喜歡你。你很特別。我們的事不要跟你爸媽說，好嗎？你可以幫我買點數卡嗎？',
        safe: '封鎖，並告訴信任的大人',
        risky: '保守祕密，幫他買點數卡',
        safeMsg: '完全正確。要你對爸媽保密、跟你要錢，都是很嚴重的警訊。',
        riskyMsg: '這是一種叫做「誘騙」或「感情詐騙」的手法。真正在乎你的人，不會要你保守祕密或給錢。',
        lesson: '「不要告訴爸媽」＋「給我錢或禮物」＝停下來、封鎖、告訴大人。這絕對不是你的錯。',
      },
      privatePhoto: {
        author: '「星探 ⭐」',
        tag: '⚠️ 請求',
        content: '你可以當模特兒喔！傳幾張你的私密照片給我，只給我看就好。我會讓你成名！',
        safe: '絕對不傳——封鎖、檢舉、告訴大人',
        risky: '傳照片，好成名',
        safeMsg: '非常好。絕對不要在網路上傳私密照片給任何人。封鎖、檢舉，然後告訴爸媽或老師。',
        riskyMsg: '停！照片可能被複製，用來傷害或威脅你。馬上告訴信任的大人——你不會被罵的。',
        lesson: '真正的星探絕不會跟小孩要私密照片。如果有人這樣做或威脅你，要告訴大人並檢舉。',
      },
      fakeNews: {
        author: '粉專「健康祕方 100%」',
        tag: '⚠️ 瘋傳貼文',
        content: '馬上分享！！！一天吃 10 顆檸檬，百病不侵！醫生都不想讓你知道這一招！！！',
        safe: '分享前先查證可靠來源',
        risky: '分享給所有人',
        safeMsg: '聰明！分享前先查查可信的來源（醫生、官方衛教網站）。',
        riskyMsg: '分享錯誤的健康資訊可能會害到別人。「醫生都不想讓你知道」是典型的假新聞用語。',
        lesson: '問問自己：是誰寫的？有證據嗎？可信的來源也這樣說嗎？如果沒有——就不要分享。',
      },
      phishing: {
        author: '「PixelBook 安全中心」',
        tag: '⚠️ 私訊',
        content: '您的帳號將在 1 小時內被刪除！請點此登入保留帳號：pixe1book-login.co',
        safe: '不理它，並檢舉這則訊息',
        risky: '趕快點進去登入',
        safeMsg: '答對了！仔細看：「pixe1book」用的是數字 1。真正的平台不會這樣威脅你。',
        riskyMsg: '那是偷你密碼的假登入頁面。快去改密碼，並告訴大人。',
        lesson: '緊急威脅＋奇怪的連結＝釣魚詐騙。自己打開真正的 App，不要點連結。',
      },
      easyMoney: {
        author: '「輕鬆打工仲介」',
        tag: '⚠️ 工作機會',
        content: '學生在家一天賺 3000 元！只要把你的銀行帳戶和手機號碼借給我們就好。',
        safe: '拒絕，並告訴大人',
        risky: '聽起來不錯，把帳戶給他們',
        safeMsg: '判斷得好。出借帳戶可能讓你在不知情的情況下變成犯罪的一份子。',
        riskyMsg: '詐騙集團會利用孩子的帳戶來轉移贓款。絕對不要出借帳戶、卡片或手機號碼。',
        lesson: '如果一份工作好賺到不像真的，那就是詐騙。絕對不要透露銀行資料。',
      },
    },
  },
};
